"use client"

import type { ReactNode, RefObject } from "react"
import { Camera, Smartphone } from "lucide-react"

import { AppStoreQrCode } from "@/components/ui/app-store-qr"
import { cn } from "@/lib/utils"
import { analytics, ctaSurfaceProps, trackEvent, type EventProperties } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { appInstallAvailableNow, useAppInstallAvailable } from "@/hooks/use-app-install-available"
import {
  APP_STORE_URL,
  appStoreCampaignToken,
  appStoreCampaignUrl,
  isAppPromoEnabled,
  isAppPromoLive,
  type AppIntent,
} from "@/lib/app-store-campaign"

export {
  appStoreCampaignToken,
  appStoreCampaignUrl,
  isAppPromoLive,
} from "@/lib/app-store-campaign"

// Impression emitter for every app placement in this file. Prefers the
// hidden-page-guarded variant so a prerendered or background-tab open cannot
// inflate the CTR denominator; falls back to the plain emitter so a partial
// analytics mock still records the impression instead of silently dropping it.
function emitCtaSurfaceShown(ctaLocation: string, props?: EventProperties) {
  const emit = analytics.ctaSurfaceShownWhenVisible ?? analytics.ctaSurfaceShown
  emit?.(ctaLocation, props)
}

// One bag for BOTH sides of every app placement. Impressions used to build
// their own object carrying `surface` while clicks built this one without it,
// so a click could not be split by the surface that produced it.
function appDownloadAnalyticsProps(
  placement: string,
  destinationUrl: string,
  opts?: {
    intent?: AppIntent
    surface?: string
    experimentVariant?: AppCtaCopyVariant | null
  },
) {
  return ctaSurfaceProps(`app_download_${placement}`, {
    surface: opts?.surface,
    destination: "app_store",
    destination_url: destinationUrl,
    placement,
    campaign_token: appStoreCampaignToken(placement),
    ...(opts?.intent ? { intent: opts.intent } : {}),
    experimentVariant: opts?.experimentVariant ?? undefined,
  })
}

// Apple glyph, matching the badge on the /app landing page (app/app/page.tsx).
export const APPLE_GLYPH_PATH =
  "M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.89-1.74.03-3.35 1.01-4.25 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.88 2.65 3.22 2.6 1.29-.05 1.78-.83 3.34-.83 1.56 0 2 .83 3.37.81 1.39-.03 2.27-1.27 3.12-2.53.98-1.45 1.39-2.85 1.41-2.93-.03-.01-2.71-1.04-2.74-4.13M14.6 4.6c.71-.86 1.19-2.06 1.06-3.25-1.02.04-2.26.68-2.99 1.54-.66.76-1.23 1.98-1.08 3.15 1.14.09 2.3-.58 3.01-1.44"

export function AppStoreBadge({
  tone = "solid",
  size = "default",
}: {
  tone?: "solid" | "glass"
  size?: "default" | "compact"
}) {
  // `glass` matches 2a's App Store pill: dark translucent + blur + hairline,
  // for use over the frosted footer wash. `solid` is the original black badge.
  const surface =
    tone === "glass"
      ? "bg-gray-900/82 backdrop-blur-md border border-white/20 shadow-[0_8px_24px_rgba(17,24,39,0.25)] hover:bg-gray-900/90"
      : "bg-gray-900 shadow-lg hover:bg-black hover:shadow-xl"
  // `compact` keeps Apple's two-line badge wording inside a single-row banner.
  // Recognition beats recall on dense surfaces, so the badge shrinks rather
  // than degrading to a text pill (growth-design, cross-cutting rule 3).
  const compact = size === "compact"
  return (
    <span
      className={cn(
        "inline-flex items-center py-1 text-white transition duration-200 hover:-translate-y-0.5",
        compact
          ? "min-h-[44px] gap-2.5 rounded-[12px] pl-4 pr-[18px]"
          : "min-h-[52px] gap-3 rounded-[14px] pl-5 pr-[22px]",
        surface,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className={compact ? "h-5 w-5" : "h-[24px] w-[24px]"}
      >
        <path d={APPLE_GLYPH_PATH} />
      </svg>
      <span className="flex flex-col text-left leading-[1.05]">
        <span
          className={cn(
            "font-medium tracking-[0.02em] opacity-80",
            compact ? "text-[10px]" : "text-[11px]",
          )}
        >
          Download on the
        </span>
        <span
          className={cn(
            "font-poppins font-bold tracking-[-0.01em]",
            compact ? "text-[15px]" : "text-[18px]",
          )}
        >
          App Store
        </span>
      </span>
    </span>
  )
}

/**
 * Bare App Store badge wrapped in a tracked store link. For chrome surfaces
 * (site footer, nav) that want the real badge — recognition over recall —
 * without the card/inline marketing framing. Self-gates like the main CTA;
 * callers can render their own pre-launch fallback (e.g. a /app text link)
 * when this returns null, checked via `isAppPromoLive`.
 */
export function AppStoreBadgeLink({
  placement,
  className,
  tone = "solid",
}: {
  placement: string
  className?: string
  tone?: "solid" | "glass"
}) {
  const destinationUrl = appStoreCampaignUrl(placement)
  // Android dead-ends on an App Store link, so it renders nothing and emits no
  // impression — an Android impression would sit in the denominator of a rate
  // whose numerator that device can never produce.
  const installAvailable = useAppInstallAvailable()
  const impressionRef = useImpressionEvent<HTMLAnchorElement>(
    `app_download_${placement}`,
    destinationUrl
      ? appDownloadAnalyticsProps(placement, destinationUrl, { surface: "app_store_badge" })
      : undefined,
    { enabled: isAppPromoLive && installAvailable && appInstallAvailableNow() && Boolean(destinationUrl), emit: emitCtaSurfaceShown },
  )
  if (!isAppPromoLive || !installAvailable || !destinationUrl) return null
  return (
    <a
      ref={impressionRef}
      href={destinationUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download What Grass Is This? on the App Store"
      onClick={() =>
        trackEvent("cta_clicked", {
          ...appDownloadAnalyticsProps(placement, destinationUrl, { surface: "app_store_badge" }),
          cta_text: "Download on the App Store",
        })
      }
      className={cn("inline-block", className)}
    >
      <AppStoreBadge tone={tone} />
    </a>
  )
}

/**
 * Unstyled-content tracked store link: caller supplies the label and the full
 * button styling via className. For bespoke surfaces (sticky bar, the limit
 * wall's compact app card) where the standard badge is too tall but the
 * gating + tracking contract must hold. Pass `children` for richer content
 * than a plain string; `label` still feeds the click event's cta_text.
 */
export function AppStoreLinkButton({
  placement,
  label,
  className,
  children,
  onClick,
}: {
  placement: string
  label: string
  className?: string
  children?: ReactNode
  /** Extra click side effect (e.g. a surface's own funnel event). Runs before the store click event. */
  onClick?: () => void
}) {
  const destinationUrl = appStoreCampaignUrl(placement)
  const installAvailable = useAppInstallAvailable()
  const impressionRef = useImpressionEvent<HTMLAnchorElement>(
    `app_download_${placement}`,
    destinationUrl
      ? appDownloadAnalyticsProps(placement, destinationUrl, { surface: "app_store_button" })
      : undefined,
    { enabled: isAppPromoLive && installAvailable && appInstallAvailableNow() && Boolean(destinationUrl), emit: emitCtaSurfaceShown },
  )
  if (!isAppPromoLive || !installAvailable || !destinationUrl) return null
  return (
    <a
      ref={impressionRef}
      href={destinationUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        onClick?.()
        trackEvent("cta_clicked", {
          ...appDownloadAnalyticsProps(placement, destinationUrl, { surface: "app_store_button" }),
          cta_text: label,
        })
      }}
      className={className}
    >
      {children ?? label}
    </a>
  )
}

/**
 * Compact text-link variant for success states where a primary triad CTA
 * (account creation / OAuth) already owns the frame. Self-gates like the main
 * CTA; renders nothing pre-launch. Quiet by design — never a badge, so it
 * can't compete with the frame's primary ask.
 */
export function AppDownloadTextLink({
  placement,
  label = "Prefer your phone? Get the free iPhone app",
  className,
}: {
  placement: string
  label?: string
  className?: string
}) {
  const destinationUrl = appStoreCampaignUrl(placement)
  const installAvailable = useAppInstallAvailable()
  const impressionRef = useImpressionEvent<HTMLAnchorElement>(
    `app_download_${placement}`,
    destinationUrl
      ? appDownloadAnalyticsProps(placement, destinationUrl, { surface: "app_store_text_link" })
      : undefined,
    { enabled: isAppPromoEnabled && installAvailable && appInstallAvailableNow() && Boolean(destinationUrl), emit: emitCtaSurfaceShown },
  )
  if (!isAppPromoEnabled || !installAvailable || !destinationUrl) return null
  return (
    <a
      ref={impressionRef}
      href={destinationUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        trackEvent("cta_clicked", {
          ...appDownloadAnalyticsProps(placement, destinationUrl, { surface: "app_store_text_link" }),
          cta_text: label,
        })
      }
      className={cn(
        "inline-flex items-center gap-1.5 text-sm font-medium text-green-700 transition-colors hover:text-green-800 hover:underline",
        className,
      )}
    >
      <Smartphone className="h-4 w-4" aria-hidden="true" />
      {label}
    </a>
  )
}

/**
 * Compact pill banner — end-of-article ask for surfaces that don't carry the
 * full card (guide pillar pages). One line of specific copy + a small badge,
 * so it closes the read without competing with the page's affiliate exits.
 * Design source: Claude Design "App Store CTA Variants" 1c.
 */
export function AppCompactBannerCTA({
  placement,
  headline = "Still not sure what grass you have?",
  subhead = "The free iPhone app tells you in seconds.",
  className,
}: {
  placement: string
  headline?: string
  subhead?: string
  className?: string
}) {
  const destinationUrl = appStoreCampaignUrl(placement)
  const installAvailable = useAppInstallAvailable()
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    `app_download_${placement}`,
    destinationUrl
      ? appDownloadAnalyticsProps(placement, destinationUrl, { surface: "app_compact_banner" })
      : undefined,
    { enabled: isAppPromoEnabled && installAvailable && appInstallAvailableNow() && Boolean(destinationUrl), emit: emitCtaSurfaceShown },
  )
  if (!isAppPromoEnabled || !installAvailable || !destinationUrl) return null
  return (
    <div ref={impressionRef} className={cn("mx-auto w-full max-w-5xl px-4 md:px-6 lg:px-8", className)}>
      {/* Wraps rather than truncates: at 390px the badge drops to its own line
          and the copy keeps the full width, so no subhead is ever clipped. */}
      <div className="flex flex-wrap items-center gap-3 rounded-3xl border border-green-600/20 bg-white/70 p-4 shadow-[0_12px_40px_rgba(0,0,0,0.04)] backdrop-blur-md sm:flex-nowrap sm:gap-4 sm:rounded-full sm:py-3 sm:pl-5 sm:pr-3">
        <img
          src="/apple-touch-icon.png"
          alt=""
          width={44}
          height={44}
          loading="lazy"
          className="h-11 w-11 flex-shrink-0 rounded-[10px]"
        />
        <div className="min-w-0 flex-1 basis-[12rem] sm:basis-auto">
          <div className="text-[15px] font-bold leading-tight tracking-[-0.02em] text-gray-900">
            {headline}
          </div>
          <div className="text-[13px] leading-snug text-gray-500">{subhead}</div>
        </div>
        <a
          href={destinationUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download What Grass Is This? on the App Store"
          onClick={() =>
            trackEvent("cta_clicked", {
              ...appDownloadAnalyticsProps(placement, destinationUrl, { surface: "app_compact_banner" }),
              cta_text: "App Store",
            })
          }
          className="inline-block flex-shrink-0"
        >
          <AppStoreBadge size="compact" />
        </a>
      </div>
    </div>
  )
}

interface AppDownloadCTAProps {
  /** Analytics placement label, e.g. "blog_footer", "grass_id_landing". */
  placement: string
  variant?: "card" | "inline"
  className?: string
  headline?: string
  subhead?: string
  inlineText?: string
  /** Stable intent dimension for result-matched web-to-app surfaces. */
  intent?: AppIntent
}

// Experiment app-cta-copy-v1 (PostHog #385140): did grass-ID-only framing beat
// whole-app framing on generic surfaces?
//
// ENDED 2026-08-08, conclusion "invalid" — the arms were never comparably
// exposed, so waiting longer could not have fixed it:
//   - 3 weeks produced 5 clicks TOTAL across both arms (control 27 impressions /
//     4 clicks, whole-app 20 / 1) against a design sized for ~55-60 clicks/week.
//   - The high-volume app-CTA surfaces pin their own copy and never read the flag
//     (by design, see the paragraph below), so only the generic surfaces enrolled.
//   - NEXT_PUBLIC_BLOG_RAIL_V2 dropped SidebarAppCard from render on 2026-08-02,
//     deleting one of the three enrolled surfaces mid-flight.
//   - `cta_surface_shown` for app_download placements only began firing
//     2026-08-07 21:26, so for almost the whole run there was no denominator.
//
// Control was hardcoded at experiment end (it was the incumbent and no variant
// shipped). The flag read was REMOVED rather than left in place because
// `experiment-end` does not modify the flag: the flag stayed active at 50/50,
// so any surviving read would keep firing exposure events against a stopped
// experiment. The flag itself has since been deleted from PostHog (verified
// absent 2026-08-11).
//
// 2026-08-11, Anthony: default copy switched to "whole-app". This is a product
// call, not a data conclusion — the invalid experiment could not adjudicate
// (see above), and his read is that generic-surface visitors want the plan /
// journal / friction-solving story, not another ID pitch. The ID story still
// wins where the reader is literally identifying grass, which is why the
// pinned intent-matched surfaces below keep their explicit grass-ID copy.
//
// Only callers that DON'T pass explicit headline/subhead use the default copy —
// intent-matched surfaces (grass-ID cluster posts, result screens) pin their own
// and always stayed out. COPY_VARIANTS keeps both arms so the grass-ID wording
// survives for any future re-test on a surface that actually gets traffic.
export const APP_CTA_COPY_FLAG = "app-cta-copy-v1"

const COPY_VARIANTS = {
  // Specificity over abstraction (growth-design): name the feature + the device,
  // not "download our app".
  control: {
    headline: "Identify your grass in seconds, on your phone",
    subhead:
      "Download the free What Grass Is This? iPhone app for instant grass ID, soil-timed reminders, and a plan tuned to your lawn.",
    inline: "Identify your grass instantly with the free iPhone app",
    sidebarTitle: "Get grass ID on your phone",
  },
  "whole-app": {
    headline: "Your lawn plan, in your pocket all season",
    subhead:
      "Grass ID, a season plan, soil-timed reminders, and a lawn journal that remembers what you did. Free on iPhone.",
    inline: "Get your lawn plan, journal, and reminders in the free iPhone app",
    sidebarTitle: "Get your lawn plan on your phone",
  },
} as const

export type AppCtaCopyVariant = keyof typeof COPY_VARIANTS

/**
 * Resolves the app-CTA copy arm. Pinned to "whole-app" (Anthony, 2026-08-11 —
 * see the note above APP_CTA_COPY_FLAG).
 *
 * Deliberately still a hook and still exported, rather than deleting it and its
 * two call sites: keeping the seam means re-running a copy test on a surface
 * with real traffic is a one-line change, and it avoids churning the
 * `experimentVariant` prop threaded through AppDownloadCTA and SidebarAppCard.
 */
export function useAppCtaCopyVariant(): AppCtaCopyVariant {
  return "whole-app"
}

const DEFAULT_HEADLINE = COPY_VARIANTS["whole-app"].headline
const DEFAULT_SUBHEAD = COPY_VARIANTS["whole-app"].subhead

export function AppDownloadCTA(props: AppDownloadCTAProps) {
  if (!isAppPromoEnabled || !APP_STORE_URL) return null
  // Explicit copy = intent-matched surface: render statically, never read the
  // flag (a flag read would log an exposure for a user who sees pinned copy).
  if (
    props.headline !== undefined
    || props.subhead !== undefined
    || props.inlineText !== undefined
  ) {
    return (
      <AppDownloadCTAPinned {...props} />
    )
  }
  return <AppDownloadCTAExperiment {...props} />
}

/**
 * Intent-matched surfaces: copy is pinned by the caller, so nothing is resolved
 * from the flag. The copy arm in force is still stamped on both the impression
 * and the click, so these placements sit in the same breakdown as the surfaces
 * that read it (mid-article blog cards used to report no arm at all).
 */
function AppDownloadCTAPinned(props: AppDownloadCTAProps) {
  const experimentVariant = useAppCtaCopyVariant()
  return (
    <AppDownloadCTABase
      {...props}
      headline={props.headline ?? DEFAULT_HEADLINE}
      subhead={props.subhead ?? DEFAULT_SUBHEAD}
      inlineText={props.inlineText ?? COPY_VARIANTS["whole-app"].inline}
      experimentVariant={experimentVariant}
    />
  )
}

function AppDownloadCTAExperiment(props: AppDownloadCTAProps) {
  const experimentVariant = useAppCtaCopyVariant()
  const copy = COPY_VARIANTS[experimentVariant]
  return (
    <AppDownloadCTABase
      {...props}
      headline={copy.headline}
      subhead={copy.subhead}
      inlineText={copy.inline}
      experimentVariant={experimentVariant}
    />
  )
}

function AppDownloadCTABase({
  placement,
  variant = "card",
  className,
  headline,
  subhead,
  inlineText,
  intent,
  experimentVariant,
}: AppDownloadCTAProps & {
  headline: string
  subhead: string
  inlineText: string
  experimentVariant: AppCtaCopyVariant | null
}) {
  const destinationUrl = appStoreCampaignUrl(placement, intent)
  const ctaLocation = `app_download_${placement}`
  const installAvailable = useAppInstallAvailable()
  const ctaProps = destinationUrl
    ? appDownloadAnalyticsProps(placement, destinationUrl, {
        intent,
        surface: variant === "inline" ? "app_inline_banner" : "app_download_card",
        experimentVariant,
      })
    : undefined
  const impressionRef = useImpressionEvent<HTMLDivElement | HTMLAnchorElement>(
    ctaLocation,
    ctaProps,
    { enabled: isAppPromoEnabled && installAvailable && appInstallAvailableNow() && Boolean(destinationUrl), emit: emitCtaSurfaceShown },
  )
  if (!isAppPromoEnabled || !installAvailable || !destinationUrl) return null

  const onClick = () =>
    trackEvent("cta_clicked", {
      ...ctaProps,
      cta_text: "Download on the App Store",
    })

  if (variant === "inline") {
    return (
      <a
        ref={impressionRef as RefObject<HTMLAnchorElement | null>}
        href={destinationUrl}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "my-8 flex flex-wrap items-center justify-between gap-4 rounded-xl bg-action p-4 shadow-md transition duration-200 hover:bg-action-hover hover:shadow-lg",
          className,
        )}
      >
        <div className="flex min-w-0 flex-[1_1_16rem] items-center gap-3">
          <div className="shrink-0 rounded-lg bg-white/20 p-2">
            <Camera className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm font-semibold text-white md:text-base">
            {inlineText}
          </span>
        </div>
        <span className="flex-shrink-0">
          <AppStoreBadge />
        </span>
      </a>
    )
  }

  // Card chrome follows Claude Design "App Store CTA Variants" 1b: white
  // glass over the page's ambient field, led by the real app icon
  // (recognition over recall) instead of an abstract camera chip.
  return (
    <div
      ref={impressionRef as RefObject<HTMLDivElement | null>}
      className={cn(
        "relative overflow-hidden rounded-3xl border border-white/70 bg-white/70 p-6 shadow-[0_12px_40px_rgba(0,0,0,0.04)] backdrop-blur-xl md:p-8",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-4 text-center">
        <img
          src="/apple-touch-icon.png"
          alt=""
          width={64}
          height={64}
          loading="lazy"
          className="h-16 w-16 rounded-[14px] shadow-md"
        />
        <div>
          <h3 className="mb-2 text-xl font-bold text-gray-900 md:text-2xl">{headline}</h3>
          <p className="mx-auto max-w-md text-sm text-gray-600 md:text-base">{subhead}</p>
        </div>
        <a href={destinationUrl} onClick={onClick} target="_blank" rel="noopener noreferrer">
          <AppStoreBadge />
        </a>
        {/* Desktop-only QR handoff — 25% of traffic is desktop, where the badge
            link dead-ends on Apple's web preview. Scanning jumps straight to
            the install sheet on the phone. Gated on input capability, not
            viewport width: shown only to fine-pointer (mouse/trackpad) devices,
            so it's hidden on every touch device — phone AND tablet — where the
            user is already on the phone and the QR is pointless. Pure CSS =
            SSR-safe, no JS device sniffing, no hydration flash (same pattern as
            components/home/upload-card.tsx). */}
        <div className="hidden items-center gap-2.5 [@media(pointer:fine)]:flex">
          <AppStoreQrCode
            placement={placement}
            intent={intent}
            size={72}
            className="h-[72px] w-[72px] rounded-lg border border-green-200 p-1"
          />
          <span className="max-w-[140px] text-left text-xs leading-snug text-gray-500">
            On your computer? Scan with your iPhone camera.
          </span>
        </div>
      </div>
    </div>
  )
}
